import download from 'downloadjs'
import i18n from '../VueI18n'
/**
 * Converte unidade do numero para literal
 * Exemplo:
 * 1 = 1
 * 10 = 10
 * 100 = 100
 * 1000 = 1 mil
 * 10000 = 10 mil
 * 100000 = 100 mil
 * 1000000 = 1 milhão
 * @param {Float / Number} valor
 */
const converterUnidadeParaLiteral = (valor) => {
	if (!isNaN(valor.replace(',', '.'))) {
		const numero = valor.toString().split('.')[0]
		if (numero.length < 4) {
			return numero
		} else if (numero.length < 7) {
			return `${numero.slice(0, -3)} mil`
		} else {
			const numInt = numero[0] + ',' + numero[1]
			if (numInt === 1) {
				return `${numInt} ${i18n.t('milhao')}`
			}
			return `${numInt} ${i18n.t('milhoes')}`
		}
	}
	return valor
}

/**
 * Calcula area de uma string que representa um espaçamento
 * @param {String} espacamento
 * @returns {Number} area do espaçamento calculado
 */
export function calculaEspacamento(espacamento) {
	return espacamento
		.split(/([Xx])/g)
		.map((m) => Number(m.replaceAll(',', '.')))
		.filter((f) => !isNaN(f))
		.reduce((a, b) => a * b)
}

/**
 * converte uma string que representa um espaçamento em um objeto
 * @param {String} espacamento
 * @returns {Object} objetos com atributos "espacamentoX" e "espacamentoY"
 */
export function espacamentoToObject(espacamento) {
	var grandezas = espacamento
		.split(/([Xx])/g)
		.map((m) => Number(m.replaceAll(',', '.')))
		.filter((f) => !isNaN(f))

	return { espacamentoX: grandezas[0], espacamentoY: grandezas[1] }
}

export function limiteMaximoMedicoesParcela(areaParcela, { espacamentoX, espacamentoY }) {
	return (areaParcela / (espacamentoX * espacamentoY)) * 1.15
}

export function limiteMinimoMedicoesParcela(areaParcela, { espacamentoX, espacamentoY }) {
	return (areaParcela / (espacamentoX * espacamentoY)) * 0.85
}

export function parseDate(date = '') {
	// if (new Date(date).isValid())
	//   return new Date(date)

	date = new Date(
		...date
			.split('/')
			.map((m) => Number(m))
			.reverse(),
	)
	return new Date(date.setMonth(date.getMonth() - 1))
}

export function yearsBetween(date1, date2) {
	return Math.ceil(Math.abs(date2 - date1) / (1000 * 60 * 60 * 24)) / 365.25
}

export const arrayToCSV = (array, filename = 'dados') => {
	if (!array || !array.length) return

	const header = Object.keys(array[0])
	const rows = array.map((row) => Object.values(row))
	// console.log(header)

	const csv = [
		header, // header row first
		...rows,
	]
		.map((m) => m.join(';'))
		.join('\r\n')

	download(csv, `${filename}.csv`, 'text/csv')
}

/**
 * Converte área em hectares para formato literal seguindo regras de precisão
 * Tabela de conversão:
 * < 1.000: inteiro com milhar (ex: 987 → "987 ha")
 * 1.000 – 9.999: X,XX mil ha (ex: 1.230 → "1,23 mil ha")
 * 10.000 – 99.999: XX,X mil ha (ex: 22.400 → "22,4 mil ha")
 * 100.000 – 999.999: XXX mil ha (ex: 88.000 → "88 mil ha")
 * 1.000.000 – 9.999.999: X,XX milhões ha (ex: 1.230.000 → "1,23 milhões ha")
 * ≥ 10.000.000: XX,X milhões ha (ex: 12.400.000 → "12,4 milhões ha")
 * @param {String | Number} valor - Valor da área em hectares
 * @returns {String} Valor formatado
 */
const converterAreaParaLiteral = (valor) => {
	// Converte para número
	const numero = typeof valor === 'string' ? parseFloat(valor.replace(',', '.')) : parseFloat(valor)

	if (isNaN(numero)) {
		return valor
	}

	// < 1.000: inteiro com milhar
	if (numero < 1000) {
		return Math.round(numero).toLocaleString('pt-BR')
	}

	// 1.000 – 9.999: X,XX mil ha
	if (numero >= 1000 && numero < 10000) {
		const valorEmMil = numero / 1000
		return `${valorEmMil.toFixed(2).replace('.', ',')} mil`
	}

	// 10.000 – 99.999: XX,X mil ha
	if (numero >= 10000 && numero < 100000) {
		const valorEmMil = numero / 1000
		return `${valorEmMil.toFixed(1).replace('.', ',')} mil`
	}

	// 100.000 – 999.999: XXX mil ha
	if (numero >= 100000 && numero < 1000000) {
		const valorEmMil = Math.round(numero / 1000)
		return `${valorEmMil} mil`
	}

	// 1.000.000 – 9.999.999: X,XX milhões ha
	if (numero >= 1000000 && numero < 10000000) {
		const valorEmMilhoes = numero / 1000000
		return `${valorEmMilhoes.toFixed(2).replace('.', ',')} ${i18n.t('milhoes')}`
	}

	// ≥ 10.000.000: XX,X milhões ha
	if (numero >= 10000000) {
		const valorEmMilhoes = numero / 1000000
		return `${valorEmMilhoes.toFixed(1).replace('.', ',')} ${i18n.t('milhoes')}`
	}

	return numero.toString()
}

/**
 * Converte volume em m³ para formato literal seguindo regras de precisão
 * Tabela de conversão:
 * < 1.000: inteiro (ex: 850 → "850")
 * 1.000 – 9.999: X,XX mil m³ (ex: 7.530 → "7,53 mil")
 * 10.000 – 99.999: XX,X mil m³ (ex: 45.200 → "45,2 mil")
 * 100.000 – 999.999: XXX mil m³ (ex: 120.000 → "120 mil")
 * 1.000.000 – 9.999.999: X,XX milhões m³ (ex: 1.900.000 → "1,90 milhões")
 * 10.000.000 – 99.999.999: XX,X milhões m³ (ex: 17.400.000 → "17,4 milhões")
 * ≥ 100.000.000: XXX milhões m³ (ex: 250.000.000 → "250 milhões")
 * @param {String | Number} valor - Valor do volume em m³
 * @returns {String} Valor formatado
 */
const converterVolumeParaLiteral = (valor) => {
	// Converte para número
	const numero = typeof valor === 'string' ? parseFloat(valor.replace(',', '.')) : parseFloat(valor)

	if (isNaN(numero)) {
		return valor
	}

	// < 1.000: inteiro
	if (numero < 1000) {
		return Math.round(numero).toLocaleString('pt-BR')
	}

	// 1.000 – 9.999: X,XX mil m³
	if (numero >= 1000 && numero < 10000) {
		const valorEmMil = numero / 1000
		return `${valorEmMil.toFixed(2).replace('.', ',')} mil`
	}

	// 10.000 – 99.999: XX,X mil m³
	if (numero >= 10000 && numero < 100000) {
		const valorEmMil = numero / 1000
		return `${valorEmMil.toFixed(1).replace('.', ',')} mil`
	}

	// 100.000 – 999.999: XXX mil m³
	if (numero >= 100000 && numero < 1000000) {
		const valorEmMil = Math.round(numero / 1000)
		return `${valorEmMil} mil`
	}

	// 1.000.000 – 9.999.999: X,XX milhões m³
	if (numero >= 1000000 && numero < 10000000) {
		const valorEmMilhoes = numero / 1000000
		return `${valorEmMilhoes.toFixed(2).replace('.', ',')} ${i18n.t('milhoes')}`
	}

	// 10.000.000 – 99.999.999: XX,X milhões m³
	if (numero >= 10000000 && numero < 100000000) {
		const valorEmMilhoes = numero / 1000000
		return `${valorEmMilhoes.toFixed(1).replace('.', ',')} ${i18n.t('milhoes')}`
	}

	// ≥ 100.000.000: XXX milhões m³
	if (numero >= 100000000) {
		const valorEmMilhoes = Math.round(numero / 1000000)
		return `${valorEmMilhoes} ${i18n.t('milhoes')}`
	}

	return numero.toString()
}

/**
 * Converte IMA (Incremento Médio Anual) para formato literal seguindo regras de precisão
 * Tabela de conversão:
 * < 1: X,XXX (ex: 0,9567 → "0,957")
 * 1 – 9,999: X,XX (ex: 7,346 → "7,35")
 * 10 – 99,9: XX,XX (ex: 27,012 → "27,01")
 * ≥ 100: XXX,X (ex: 123,44 → "123,4")
 * @param {String | Number} valor - Valor do IMA
 * @returns {String} Valor formatado
 */
const converterIMAParaLiteral = (valor) => {
	// Converte para número
	const numero = typeof valor === 'string' ? parseFloat(valor.replace(',', '.')) : parseFloat(valor)

	if (isNaN(numero)) {
		return valor
	}

	// < 1: X,XXX
	if (numero < 1) {
		return numero.toFixed(3).replace('.', ',')
	}

	// 1 – 9,999: X,XX
	if (numero >= 1 && numero < 10) {
		return numero.toFixed(2).replace('.', ',')
	}

	// 10 – 99,9: XX,XX
	if (numero >= 10 && numero < 100) {
		return numero.toFixed(2).replace('.', ',')
	}

	// ≥ 100: XXX,X
	if (numero >= 100) {
		return numero.toFixed(1).replace('.', ',')
	}

	return numero.toString()
}

/**
 * Formata um número em pt-BR com separadores de milhar e duas casas decimais.
 * Usado como tooltip ao lado dos KPIs para exibir o valor cru sem máscaras
 * de abreviação ("12,4 milhões" → "12.400.000,00").
 */
const formatarValorCompleto = (valor) => {
	if (valor == null) return ''
	const numero = typeof valor === 'string' ? parseFloat(valor.replace(',', '.')) : Number(valor)
	if (isNaN(numero)) return ''
	return numero.toLocaleString('pt-BR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

/**
 * Backend envia a faixa de idade como "X - Y" (idade mais nova - idade mais antiga).
 * Quando os extremos coincidem (ex.: "3 - 3"), retorna apenas um valor.
 */
const colapsarFaixaIdade = (faixa) => {
	if (faixa == null) return ''
	const partes = String(faixa)
		.split('-')
		.map((p) => p.trim())
	if (partes.length === 2 && partes[0] === partes[1]) {
		return partes[0]
	}
	return faixa
}

export {
	converterUnidadeParaLiteral,
	converterAreaParaLiteral,
	converterVolumeParaLiteral,
	converterIMAParaLiteral,
	formatarValorCompleto,
	colapsarFaixaIdade,
}
